const STORE_DOMAIN = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN as string;
const ACCESS_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN as string;

const STOREFRONT_API_VERSION = '2024-10';
const CART_ID_STORAGE_KEY = 'shopify_cart_id';
const CART_UPDATED_EVENT = 'cart-updated';

async function shopifyFetch<T = unknown>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const url = `https://${STORE_DOMAIN}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`Shopify API error: ${res.status} ${res.statusText}`);
  }

  const json = await res.json();
  if (json.errors) {
    throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
  }
  return json.data as T;
}

// ─── バリアント取得 ──────────────────────────────────────────────
interface VariantNode { id: string }
interface ProductVariantsResponse {
  product: {
    variants: {
      edges: { node: VariantNode }[];
    };
  } | null;
}

const variantCache = new Map<string, string>();

function toProductGid(shopifyProductId: string): string {
  return shopifyProductId.startsWith('gid://')
    ? shopifyProductId
    : `gid://shopify/Product/${shopifyProductId}`;
}

export async function getFirstVariantId(shopifyProductId: string): Promise<string> {
  const cached = variantCache.get(shopifyProductId);
  if (cached) return cached;

  const data = await shopifyFetch<ProductVariantsResponse>(
    `query getProduct($id: ID!) {
      product(id: $id) {
        variants(first: 1) {
          edges { node { id } }
        }
      }
    }`,
    { id: toProductGid(shopifyProductId) },
  );

  if (!data.product || data.product.variants.edges.length === 0) {
    throw new Error('商品またはバリアントが見つかりません');
  }

  const variantId = data.product.variants.edges[0].node.id;
  variantCache.set(shopifyProductId, variantId);
  return variantId;
}

export interface SelectedOption { name: string; value: string }

// SKU → variantId のキャッシュ（同一商品の全バリアントを1回で取得しキャッシュ）
const skuMapCache = new Map<string, Map<string, string>>();

async function loadVariantsBySKU(shopifyProductId: string): Promise<Map<string, string>> {
  const cached = skuMapCache.get(shopifyProductId);
  if (cached) return cached;
  const data = await shopifyFetch<{
    product: { variants: { edges: { node: { id: string; sku: string | null } }[] } } | null;
  }>(
    `query getVariants($id: ID!) {
      product(id: $id) {
        variants(first: 100) {
          edges { node { id sku } }
        }
      }
    }`,
    { id: toProductGid(shopifyProductId) },
  );
  const map = new Map<string, string>();
  for (const edge of data.product?.variants?.edges ?? []) {
    if (edge.node.sku) map.set(edge.node.sku.trim(), edge.node.id);
  }
  skuMapCache.set(shopifyProductId, map);
  return map;
}

/**
 * 品番 (SKU) で Shopify バリアントID を解決。
 * 同一productの全バリアントを1度だけ取得しキャッシュ。
 * 見つからない場合は null を返す（呼び出し側でフォールバック判断）。
 */
export async function getVariantIdBySKU(
  shopifyProductId: string,
  sku: string,
): Promise<string | null> {
  if (!sku) return null;
  try {
    const map = await loadVariantsBySKU(shopifyProductId);
    return map.get(sku.trim()) || null;
  } catch {
    return null;
  }
}

/**
 * プルダウンで選択中のオプションに合致する Shopify バリアントID を取得。
 * 見つからない場合は最初のバリアントを返す。
 */
export async function getVariantIdByOptions(
  shopifyProductId: string,
  options: SelectedOption[],
): Promise<string> {
  if (!options || options.length === 0) {
    return getFirstVariantId(shopifyProductId);
  }
  const cleaned = options.filter(o => o.name && o.value);
  if (cleaned.length === 0) {
    return getFirstVariantId(shopifyProductId);
  }
  try {
    const data = await shopifyFetch<{
      product: { variantBySelectedOptions: { id: string } | null } | null;
    }>(
      `query getVariant($id: ID!, $options: [SelectedOptionInput!]!) {
        product(id: $id) {
          variantBySelectedOptions(selectedOptions: $options) { id }
        }
      }`,
      { id: toProductGid(shopifyProductId), options: cleaned },
    );
    const id = data.product?.variantBySelectedOptions?.id;
    if (id) return id;
  } catch {
    // フォールスルー
  }
  return getFirstVariantId(shopifyProductId);
}

// ─── 永続カート ──────────────────────────────────────────────────
export interface CartLineAttribute { key: string; value: string }

export interface CartLine {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    image?: { url: string };
    product: { title: string; handle: string; featuredImage?: { url: string } };
  };
  cost: {
    totalAmount: { amount: string; currencyCode: string };
  };
  attributes?: CartLineAttribute[];
}

export interface Cart {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  lines: CartLine[];
  cost: {
    totalAmount: { amount: string; currencyCode: string };
    subtotalAmount: { amount: string; currencyCode: string };
  };
}

interface CartResponse {
  id: string;
  checkoutUrl: string;
  totalQuantity: number;
  lines: { edges: { node: CartLine }[] };
  cost: {
    totalAmount: { amount: string; currencyCode: string };
    subtotalAmount: { amount: string; currencyCode: string };
  };
}

const CART_QUERY_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost {
    totalAmount { amount currencyCode }
    subtotalAmount { amount currencyCode }
  }
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        attributes { key value }
        cost { totalAmount { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id
            title
            image { url }
            product { title handle featuredImage { url } }
          }
        }
      }
    }
  }
`;

function normalizeCart(c: CartResponse): Cart {
  return {
    id: c.id,
    checkoutUrl: c.checkoutUrl,
    totalQuantity: c.totalQuantity,
    cost: c.cost,
    lines: c.lines.edges.map(e => e.node),
  };
}

function getStoredCartId(): string | null {
  try { return localStorage.getItem(CART_ID_STORAGE_KEY); } catch { return null; }
}
function setStoredCartId(id: string) {
  try { localStorage.setItem(CART_ID_STORAGE_KEY, id); } catch { /* ignore */ }
}
function clearStoredCartId() {
  try { localStorage.removeItem(CART_ID_STORAGE_KEY); } catch { /* ignore */ }
}

function emitCartUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
  }
}

/** 既存カートを取得（無効/期限切れなら null） */
export async function getCart(): Promise<Cart | null> {
  const id = getStoredCartId();
  if (!id) return null;
  try {
    const data = await shopifyFetch<{ cart: CartResponse | null }>(
      `query getCart($id: ID!) { cart(id: $id) { ${CART_QUERY_FIELDS} } }`,
      { id },
    );
    if (!data.cart) {
      clearStoredCartId();
      return null;
    }
    return normalizeCart(data.cart);
  } catch {
    return null;
  }
}

/** カート行追加時に付与する追加メタ情報（任意） */
export interface CartLineMeta {
  /** バリアントの代表画像URL (Shopifyのvariant.imageが取れない時の表示用) */
  imageUrl?: string;
  /** カラー等のオプション表示用ラベル (例: "#4V6 ベージュ") */
  optionLabel?: string;
}

function buildLineAttributes(meta?: CartLineMeta): CartLineAttribute[] {
  const attrs: CartLineAttribute[] = [];
  if (meta?.imageUrl) attrs.push({ key: '_image_url', value: meta.imageUrl });
  if (meta?.optionLabel) attrs.push({ key: '_option_label', value: meta.optionLabel });
  return attrs;
}

/** 新規カート作成 */
async function createCart(variantId: string, quantity: number, meta?: CartLineMeta): Promise<Cart> {
  const sourceOrigin = window.location.origin;
  const sourceUrl = `${sourceOrigin}${__BASE_PATH__}`;

  const lineAttrs = buildLineAttributes(meta);

  const data = await shopifyFetch<{
    cartCreate: { cart: CartResponse | null; userErrors: { message: string }[] };
  }>(
    `mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ${CART_QUERY_FIELDS} }
        userErrors { field message }
      }
    }`,
    {
      input: {
        lines: [{
          merchandiseId: variantId,
          quantity,
          ...(lineAttrs.length > 0 ? { attributes: lineAttrs } : {}),
        }],
        attributes: [
          { key: 'source_origin', value: sourceOrigin },
          { key: 'source_url', value: sourceUrl },
        ],
      },
    },
  );

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors.map(e => e.message).join(', '));
  }
  if (!data.cartCreate.cart) throw new Error('カートの作成に失敗しました');

  const cart = normalizeCart(data.cartCreate.cart);
  setStoredCartId(cart.id);
  return cart;
}

/** カートに行を追加（既存カートがあればそこに追加、なければ新規作成）
 *  既存カートに同じバリアントIDのラインが既にある場合は、そのラインの
 *  数量を quantity 分だけ増やす（cartLinesUpdate を使用）。
 *  在庫超過などのエラーは throw して呼び出し側で表示する。
 *  「カートID自体が無効」と判定された場合のみ既存カートを破棄する */
export async function addToCart(variantId: string, quantity = 1, meta?: CartLineMeta): Promise<Cart> {
  const lineAttrs = buildLineAttributes(meta);
  const existingId = getStoredCartId();

  if (existingId) {
    // 既存カートを取得 (取得自体が失敗 = カート無効と判断)
    let current: Cart | null = null;
    try {
      current = await getCart();
    } catch {
      current = null;
    }

    if (current) {
      // 同一バリアントのライン検索
      const sameLine = current.lines.find(l => l.merchandise.id === variantId);
      if (sameLine) {
        // 既存ライン: 加算
        const updated = await updateCartLine(sameLine.id, sameLine.quantity + quantity);
        if (updated) return updated;
        // updateが null を返した = ユーザーエラー(在庫超過等)。throwしてカートは保持
        throw new Error('在庫数を超えているため数量を増やせませんでした');
      }

      // 同一バリアントなし → 既存カートに新規ライン追加
      const data = await shopifyFetch<{
        cartLinesAdd: { cart: CartResponse | null; userErrors: { field: string[]; message: string }[] };
      }>(
        `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { ${CART_QUERY_FIELDS} }
            userErrors { field message }
          }
        }`,
        {
          cartId: existingId,
          lines: [{
            merchandiseId: variantId,
            quantity,
            ...(lineAttrs.length > 0 ? { attributes: lineAttrs } : {}),
          }],
        },
      );
      if (data.cartLinesAdd.userErrors.length > 0) {
        // 在庫不足等のユーザーエラー: カートは保持して throw
        throw new Error(data.cartLinesAdd.userErrors.map(e => e.message).join(' / '));
      }
      if (data.cartLinesAdd.cart) {
        const cart = normalizeCart(data.cartLinesAdd.cart);
        emitCartUpdated();
        return cart;
      }
      throw new Error('カート追加レスポンスが空でした');
    }

    // current が null = カートID自体が無効/期限切れ
    clearStoredCartId();
  }

  // 新規カート作成
  const cart = await createCart(variantId, quantity, meta);
  emitCartUpdated();
  return cart;
}

/** 行の数量を更新。エラー (在庫超過等) は throw。カートID無効は null 返却。*/
export async function updateCartLine(lineId: string, quantity: number): Promise<Cart | null> {
  const cartId = getStoredCartId();
  if (!cartId) return null;
  const data = await shopifyFetch<{
    cartLinesUpdate: { cart: CartResponse | null; userErrors: { field: string[]; message: string }[] };
  }>(
    `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ${CART_QUERY_FIELDS} }
        userErrors { field message }
      }
    }`,
    { cartId, lines: [{ id: lineId, quantity }] },
  );
  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors.map(e => e.message).join(' / '));
  }
  if (!data.cartLinesUpdate.cart) return null;
  emitCartUpdated();
  return normalizeCart(data.cartLinesUpdate.cart);
}

/** 行を削除。userErrors / network error は throw */
export async function removeCartLine(lineId: string): Promise<Cart | null> {
  const cartId = getStoredCartId();
  if (!cartId) return null;
  const data = await shopifyFetch<{
    cartLinesRemove: { cart: CartResponse | null; userErrors: { field: string[]; message: string }[] };
  }>(
    `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ${CART_QUERY_FIELDS} }
        userErrors { field message }
      }
    }`,
    { cartId, lineIds: [lineId] },
  );
  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors.map(e => e.message).join(' / '));
  }
  if (!data.cartLinesRemove.cart) return null;
  emitCartUpdated();
  return normalizeCart(data.cartLinesRemove.cart);
}

/** カートを破棄（チェックアウト完了後などに呼ぶ。手動クリア用途） */
export function clearLocalCart() {
  clearStoredCartId();
  emitCartUpdated();
}

/** カート変更イベントを購読 */
export function onCartUpdated(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(CART_UPDATED_EVENT, handler);
  return () => window.removeEventListener(CART_UPDATED_EVENT, handler);
}

export function isShopifyConfigured(): boolean {
  return Boolean(STORE_DOMAIN && ACCESS_TOKEN);
}
